/**
 * 개인 주간 보고. 참여 중인 플랜을 그 주의 라이브 데이터로 묶는다.
 * 저장하는 것은 빼기(excludedIds)와 한 줄 메모뿐이다. 항목 자체는 다시 계산한다.
 */
import { addDays, isISODate, toDay, weekdayOfDay, type ISODate } from "@/engine/date";
import type { ExecutionTask, Project, ProjectRecord, Task, UserResource } from "@/engine/types";
import { computeSchedule } from "@/engine/cpm";
import { WorkCalendar } from "@/engine/calendar";
import { formatDateRange } from "@/lib/date-format";
import { recordPhase, riskBand, riskScore } from "@/store/record-ops";

export interface WeekWindow {
  start: ISODate;
  end: ISODate;
}

export interface WeeklyActor {
  /** 실행 담당·WBS 담당에 적힌 표시 이름. */
  name: string;
  /** 알면 배정의 자원 id. 없으면 이름과 같은 자원으로 찾는다. */
  resourceId?: string;
}

export type WeeklyKind = "exec" | "wbs" | "record";
export type WeeklyBucket = "done" | "inProgress" | "next" | "blockers";

export interface UserWeeklyItem {
  id: string;
  projectId: string;
  projectName: string;
  kind: WeeklyKind;
  bucket: WeeklyBucket;
  title: string;
  meta: string;
  excluded: boolean;
}

export interface UserWeeklyPlan {
  projectId: string;
  projectName: string;
  /** 배정이 없으면 null. 여러 배정은 합이다. */
  allocationPct: number | null;
  plannedMM: number;
}

export interface UserWeeklyKpi {
  done: number;
  inProgress: number;
  next: number;
  resolvedIssues: number;
  plannedMM: number;
}

export interface UserWeeklyNote {
  excludedIds: string[];
  memo: string;
}

export interface UserWeeklyReport {
  actorName: string;
  week: WeekWindow;
  plans: UserWeeklyPlan[];
  items: UserWeeklyItem[];
  kpi: UserWeeklyKpi;
  memo: string;
}

export const EMPTY_WEEKLY_NOTE: UserWeeklyNote = { excludedIds: [], memo: "" };

const SECTION_LABEL = {
  done: "1. 금주 완료",
  inProgress: "2. 진행 중",
  next: "3. 차주 계획",
  blockers: "4. 지연/블로커",
} as const;

const BUCKETS: WeeklyBucket[] = ["done", "inProgress", "next", "blockers"];

/** 그 날짜가 들어 있는 월~일. */
export function weekWindowContaining(anchor: ISODate): WeekWindow {
  const wd = weekdayOfDay(toDay(anchor));
  const start = addDays(anchor, wd === 0 ? -6 : 1 - wd);
  return { start, end: addDays(start, 6) };
}

/** deltaWeeks가 양수면 앞으로, 음수면 뒤로. */
export function shiftWeek(week: WeekWindow, deltaWeeks: number): WeekWindow {
  const start = addDays(week.start, deltaWeeks * 7);
  return { start, end: addDays(start, 6) };
}

export function weeklyNoteKey(actorName: string, weekStart: ISODate): string {
  return `${actorName}\u001f${weekStart}`;
}

/** 한 플랜에서 주간 보고의 담당자로 고를 수 있는 사람. */
export function actorsInProject(
  project: Pick<Project, "resourceAssignments" | "executionTasks" | "tasks">,
  resources: Record<string, Pick<UserResource, "id" | "displayName">>,
): WeeklyActor[] {
  const byName = new Map<string, WeeklyActor>();
  const add = (name?: string, resourceId?: string) => {
    const text = name?.trim();
    if (!text) return;
    const prev = byName.get(text);
    const id = resourceId?.trim() || prev?.resourceId;
    byName.set(text, id ? { name: text, resourceId: id } : { name: text });
  };
  for (const row of project.resourceAssignments ?? []) add(resources[row.resourceId]?.displayName, row.resourceId);
  for (const task of project.executionTasks ?? []) for (const name of task.assignees) add(name, resourcesByName(resources, name));
  for (const task of project.tasks) add(task.assignee, task.assignedResourceId);
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name, "ko") || a.name.localeCompare(b.name));
}

function resourcesByName(resources: Record<string, Pick<UserResource, "id" | "displayName">>, name: string): string | undefined {
  return Object.values(resources).find((resource) => resource.displayName.trim() === name)?.id;
}

export function normalizeWeeklyNote(raw: unknown): UserWeeklyNote {
  const note = (raw ?? {}) as Partial<UserWeeklyNote>;
  const excludedIds = Array.isArray(note.excludedIds)
    ? [...new Set(note.excludedIds.filter((id): id is string => typeof id === "string" && id.trim().length > 0))]
    : [];
  const memo = typeof note.memo === "string" ? note.memo.replace(/\s+/g, " ").trim().slice(0, 280) : "";
  return { excludedIds, memo };
}

/** 열린 편집본이 카탈로그에 있으면 그 플랜만 바꾼다. 카탈로그에 없는 id는 넣지 않는다. */
export function mergeLiveProject(projects: readonly Project[], live: Project | null | undefined): Project[] {
  if (!live) return [...projects];
  return projects.map((project) => (project.id === live.id ? live : project));
}

function dayOf(value: string | undefined): ISODate | null {
  if (!value) return null;
  const day = value.slice(0, 10);
  return isISODate(day) ? day : null;
}

function inRange(day: ISODate | null, start: ISODate, end: ISODate): boolean {
  return !!day && day >= start && day <= end;
}

/** 그 주를 보는 시점. 지난 주는 일요일 다음 날, 이번 주는 오늘, 다음 주는 월요일. */
export function overdueAsOf(week: WeekWindow, today: ISODate): ISODate {
  if (today < week.start) return week.start;
  if (today > week.end) return addDays(week.end, 1);
  return today;
}

function resourceIdsFor(actor: WeeklyActor, resources: Record<string, Pick<UserResource, "id" | "displayName">> | undefined): Set<string> {
  const ids = new Set<string>();
  if (actor.resourceId) ids.add(actor.resourceId);
  const name = actor.name.trim();
  if (!name || !resources) return ids;
  for (const resource of Object.values(resources)) {
    if (resource.displayName.trim() === name) ids.add(resource.id);
  }
  return ids;
}

function named(value: string | undefined, actorName: string, ids: ReadonlySet<string>): boolean {
  const text = value?.trim();
  if (!text) return false;
  return text === actorName || ids.has(text);
}

function ownsExec(task: ExecutionTask, actorName: string, ids: ReadonlySet<string>): boolean {
  return task.assignees.some((name) => named(name, actorName, ids));
}

function ownsWbs(task: Task, actorName: string, ids: ReadonlySet<string>): boolean {
  return named(task.assignee, actorName, ids) || (!!task.assignedResourceId && ids.has(task.assignedResourceId));
}

function ownsRecord(record: ProjectRecord, actorName: string, ids: ReadonlySet<string>): boolean {
  return record.assignees.some((name) => named(name, actorName, ids)) || named(record.author, actorName, ids);
}

function hotRecord(record: ProjectRecord): boolean {
  if (record.severity === "high") return true;
  const score = riskScore(record);
  if (score == null) return false;
  const band = riskBand(score);
  return band === "high" || band === "critical";
}

function itemId(projectId: string, kind: WeeklyKind, sourceId: string): string {
  return `${projectId}:${kind}:${sourceId}`;
}

function pushItem(items: UserWeeklyItem[], item: Omit<UserWeeklyItem, "excluded">) {
  items.push({ ...item, excluded: false });
}

function kpiOf(items: readonly UserWeeklyItem[], plans: readonly UserWeeklyPlan[]): UserWeeklyKpi {
  const visible = items.filter((item) => !item.excluded);
  const count = (bucket: WeeklyBucket) => visible.filter((item) => item.bucket === bucket).length;
  const plannedMM = plans.reduce((sum, plan) => sum + plan.plannedMM, 0);
  return {
    done: count("done"),
    inProgress: count("inProgress"),
    next: count("next"),
    resolvedIssues: visible.filter((item) => item.bucket === "done" && item.kind === "record").length,
    plannedMM: Math.round(plannedMM * 100) / 100,
  };
}

export function buildUserWeekly(input: {
  projects: readonly Project[];
  live?: Project | null;
  actor: WeeklyActor;
  resources?: Record<string, Pick<UserResource, "id" | "displayName">>;
  week: WeekWindow;
  today: ISODate;
  note?: Partial<UserWeeklyNote> | null;
}): UserWeeklyReport {
  const actorName = input.actor.name.trim();
  const ids = resourceIdsFor({ ...input.actor, name: actorName }, input.resources);
  const note = normalizeWeeklyNote(input.note);
  const excluded = new Set(note.excludedIds);
  const next = shiftWeek(input.week, 1);
  const asOf = overdueAsOf(input.week, input.today);
  const projects = mergeLiveProject(input.projects, input.live).filter((project) => project.lifecycle !== "trashed");
  const plans: UserWeeklyPlan[] = [];
  const items: UserWeeklyItem[] = [];

  for (const project of projects) {
    const assignments = (project.resourceAssignments ?? []).filter((row) => ids.has(row.resourceId));
    const allocation = assignments.reduce((sum, row) => sum + (Number.isFinite(row.allocationRatio) ? row.allocationRatio : 0), 0);
    const plannedMM = assignments.reduce((sum, row) => sum + (Number.isFinite(row.plannedMM) ? row.plannedMM : 0), 0);
    const participating = assignments.length > 0;
    const before = items.length;

    for (const task of project.executionTasks ?? []) {
      if (!ownsExec(task, actorName, ids)) continue;
      const base = { projectId: project.id, projectName: project.name, kind: "exec" as const, title: task.title || "제목 없음" };
      const completed = dayOf(task.completedAt);
      if (task.status === "done" && inRange(completed, input.week.start, input.week.end)) {
        pushItem(items, { ...base, id: itemId(project.id, "exec", task.id), bucket: "done", meta: "완료" });
        continue;
      }
      if (task.status === "done") continue;
      const overdue = !!task.due && task.due < asOf;
      if (task.status === "blocked" || overdue) {
        pushItem(items, {
          ...base,
          id: itemId(project.id, "exec", task.id),
          bucket: "blockers",
          meta: task.status === "blocked" ? "막힘" : "기한 지남",
        });
        continue;
      }
      if (task.status === "inProgress") {
        pushItem(items, { ...base, id: itemId(project.id, "exec", task.id), bucket: "inProgress", meta: "진행" });
        continue;
      }
      if (task.due && inRange(task.due, next.start, next.end)) {
        pushItem(items, { ...base, id: itemId(project.id, "exec", task.id), bucket: "next", meta: "차주 마감" });
      }
    }

    let schedule: ReturnType<typeof computeSchedule> | null = null;
    try {
      schedule = computeSchedule(project, new WorkCalendar(project.calendar));
    } catch {
      schedule = null;
    }
    if (schedule) {
      const byId = new Map(project.tasks.map((task) => [task.id, task]));
      for (const id of schedule.order) {
        const scheduled = schedule.tasks.get(id);
        const task = byId.get(id);
        if (!scheduled || !task || scheduled.isSummary) continue;
        if (!ownsWbs(task, actorName, ids)) continue;
        const base = { projectId: project.id, projectName: project.name, kind: "wbs" as const, title: task.name || "이름 없음" };
        if (scheduled.percentComplete >= 100 && inRange(scheduled.finish, input.week.start, input.week.end)) {
          pushItem(items, { ...base, id: itemId(project.id, "wbs", task.id), bucket: "done", meta: "WBS 100%" });
          continue;
        }
        if (scheduled.percentComplete >= 100) continue;
        if (scheduled.percentComplete > 0) {
          pushItem(items, {
            ...base,
            id: itemId(project.id, "wbs", task.id),
            bucket: "inProgress",
            meta: `${Math.round(scheduled.percentComplete)}%`,
          });
          continue;
        }
        if (inRange(scheduled.finish, next.start, next.end)) {
          pushItem(items, { ...base, id: itemId(project.id, "wbs", task.id), bucket: "next", meta: "차주 종료" });
        }
      }
    }

    for (const record of project.records ?? []) {
      if (!ownsRecord(record, actorName, ids)) continue;
      const base = { projectId: project.id, projectName: project.name, kind: "record" as const, title: record.title || "제목 없음" };
      const closed = dayOf(record.closedAt);
      if (recordPhase(record.status) === "closed" && inRange(closed, input.week.start, input.week.end)) {
        pushItem(items, { ...base, id: itemId(project.id, "record", record.id), bucket: "done", meta: "종결" });
        continue;
      }
      if (recordPhase(record.status) === "closed") continue;
      if (!hotRecord(record)) continue;
      const score = riskScore(record);
      const meta = score != null && riskBand(score) === "critical" ? "매우 높음" : "높음";
      pushItem(items, { ...base, id: itemId(project.id, "record", record.id), bucket: "blockers", meta });
    }

    if (participating || items.length > before) {
      plans.push({
        projectId: project.id,
        projectName: project.name,
        allocationPct: assignments.length ? Math.round(allocation * 100) : null,
        plannedMM: Math.round(plannedMM * 100) / 100,
      });
    }
  }

  plans.sort((a, b) => a.projectName.localeCompare(b.projectName, "ko") || a.projectId.localeCompare(b.projectId));
  items.sort((a, b) => a.projectName.localeCompare(b.projectName, "ko") || a.title.localeCompare(b.title, "ko") || a.id.localeCompare(b.id));
  const marked = items.map((item) => (excluded.has(item.id) ? { ...item, excluded: true } : item));
  return {
    actorName,
    week: input.week,
    plans,
    items: marked,
    kpi: kpiOf(marked, plans),
    memo: note.memo,
  };
}

export function toggleWeeklyExclusion(note: UserWeeklyNote, itemId: string): UserWeeklyNote {
  const excludedIds = note.excludedIds.includes(itemId)
    ? note.excludedIds.filter((id) => id !== itemId)
    : [...note.excludedIds, itemId];
  return normalizeWeeklyNote({ ...note, excludedIds });
}

function sectionLines(report: UserWeeklyReport, bucket: WeeklyBucket): string[] {
  const rows = report.items.filter((item) => item.bucket === bucket && !item.excluded);
  if (!rows.length) return ["없음"];
  return rows.map((item) => `- ${item.projectName} · ${item.title}`);
}

function exportBody(report: UserWeeklyReport, heading: (label: string) => string): string {
  const range = formatDateRange(report.week.start, report.week.end, "numeric");
  const lines = [`[주간 보고] ${range} · ${report.actorName}`, ""];
  for (const bucket of BUCKETS) {
    lines.push(heading(SECTION_LABEL[bucket]));
    lines.push(...sectionLines(report, bucket));
    lines.push("");
  }
  lines.push(heading("5. 특이사항"));
  lines.push(report.memo.trim() || "없음");
  return lines.join("\n");
}

/** 슬랙에 붙여 넣는 마크다운. 뺀 항목은 빠진다. */
export function formatUserWeeklyMarkdown(report: UserWeeklyReport): string {
  return exportBody(report, (label) => `**${label}**`);
}

/** 같은 다섯 절의 일반 텍스트. */
export function formatUserWeeklyPlain(report: UserWeeklyReport): string {
  return exportBody(report, (label) => label);
}
